import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { policyService, getErrorMessage } from '@/services';
import type { RecordInstallmentPaymentRequest } from '@/services/policies';
import { renewalRefusalOf } from '@/services/policies';
import { RENEWAL_CUSTOMER_MISMATCH, type PolicyCreateRequest, type PolicyFilters } from '@/types';

export function usePolicies(
  page: number = 1,
  filters?: PolicyFilters,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['policies', page, filters],
    queryFn: () => policyService.getPolicies(page, filters),
    placeholderData: (previousData) => previousData,
    enabled: options?.enabled ?? true,
  });
}

export function usePolicy(id: string) {
  return useQuery({
    queryKey: ['policy', id],
    queryFn: () => policyService.getPolicy(id),
    enabled: !!id,
  });
}

/**
 * Item C5: the Insurance Type / Insurance Company / Product options actually
 * present on the CALLER's own scoped book (`/policies/filter-options/`,
 * `PolicyViewSet.filter_options`) -- used by PoliciesListPage in place of the
 * platform-wide `useInsurersLookup`/`useInsuranceTypesLookup`/
 * `useProductsLookup` trio for a customer login, whose filter dropdowns must
 * not leak every insurer/type/product on the platform. `enabled` lets the
 * page opt in only for that role, so every other role keeps the unscoped
 * catalogue it already had (unaffected by this hook's existence).
 */
export function useCustomerPolicyFilterOptions(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['policies', 'filter-options'],
    queryFn: () => policyService.getFilterOptions(),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreatePolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PolicyCreateRequest) => policyService.createPolicy(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      toast.success('Policy created successfully');
    },
    onError: (error) => {
      // B9: a customer mismatch on a renewal link is a QUESTION -- the page
      // asks the broker to confirm and resubmits. An error toast over that
      // prompt would read as a failure.
      if (renewalRefusalOf(error)?.code === RENEWAL_CUSTOMER_MISMATCH) return;
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdatePolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PolicyCreateRequest> }) =>
      policyService.updatePolicy(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['policy', variables.id] });
      toast.success('Policy updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useDeletePolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => policyService.deletePolicy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      toast.success('Policy deleted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// Item 4 — marks payment_status='completed' for accounting; does not touch
// policy status. Invalidates both the list and the single-policy cache so
// the badge flips to Completed wherever it's shown.
export function useMarkPaymentReceived() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => policyService.markPaymentReceived(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['policy', id] });
      toast.success('Payment marked as received');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/** Move a policy's status, with the broker's reason recorded against it. */
export function useUpdatePolicyStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: string; notes?: string }) =>
      policyService.updateStatus(id, status, notes),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['policy', id] });
      queryClient.invalidateQueries({ queryKey: ['policy-timeline', id] });
      toast.success('Status updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function usePolicyTimeline(policyId: string, enabled = true) {
  return useQuery({
    queryKey: ['policy-timeline', policyId],
    queryFn: () => policyService.getPolicyTimeline(policyId),
    enabled: enabled && !!policyId,
  });
}

/**
 * Upfront vehicle lookup (round-2 Wave F2, item 3b) — the motor wizard's
 * RegistrationNumberStep calls this once a syntactically valid, debounced
 * registration number is entered, to prefill make/model/variant + prior-
 * policy details from an existing PolicyVehicle/CustomerVehicle match.
 * `enabled` additionally gates the query (e.g. skip while "Registration
 * Pending" is checked, or the value hasn't settled yet).
 */
/**
 * The policy being renewed, shaped for the motor wizard's fields.
 *
 * `source` is a Policy id or a Renewal id -- the two screens that link to
 * `/quotes/new?renewal=` pass different ones, and the server resolves either.
 */
export function useRenewalPrefill(source: string) {
  return useQuery({
    queryKey: ['renewal-prefill', source],
    queryFn: () => policyService.getRenewalPrefill(source),
    enabled: !!source,
    // The policy being renewed does not change under the broker mid-wizard,
    // and a refetch would re-run the prefill effect against fields they have
    // since edited.
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * B9 -- the vehicle duplicate check, fired on an explicit moment (blur of the
 * Vehicle Number box, submit, or a settled registration number in the motor
 * wizard) rather than on render. Mutation-style so the caller decides when,
 * and gets the answer back in the call site that must act on it.
 *
 * No toast on failure: the check is advisory, and the caller shows its own
 * "could not check" note rather than a generic error over the form.
 */
export function useVehicleCheck() {
  return useMutation({
    mutationFn: ({ vehicleNumber, excludePolicyId }: { vehicleNumber: string; excludePolicyId?: string }) =>
      policyService.checkVehicle(vehicleNumber, excludePolicyId),
  });
}

export function useVehicleRegistrationLookup(
  registrationNumber: string,
  enabled: boolean,
  modelId?: string,
) {
  return useQuery({
    // `modelId` is part of the key: correcting the model asks a different
    // question and must not be served the uncorrected cached answer.
    queryKey: ['vehicle-registration-lookup', registrationNumber, modelId ?? ''],
    queryFn: () => policyService.lookupVehicle(registrationNumber, { modelId }),
    enabled: enabled && registrationNumber.length >= 4,
  });
}

/**
 * Search a vendor's RC records for a vehicle we do not already hold.
 *
 * Deliberately a mutation, not a query: this is the only path that reaches a
 * paid vendor, so it must fire on an explicit click rather than on render or
 * on a debounced keystroke. The result is written into the free lookup's cache
 * so the rest of the step reads it exactly as it would a free hit.
 */
export function useExternalVehicleLookup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (registrationNumber: string) =>
      policyService.lookupVehicle(registrationNumber, { external: true }),
    onSuccess: (data, registrationNumber) => {
      queryClient.setQueryData(['vehicle-registration-lookup', registrationNumber], data);
      if (!data.found) toast.error('No external record found for this registration.');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Record which variant the broker chose for this vehicle.
 *
 * Fire-and-forget from the UI's point of view: the form already holds the
 * choice, so nothing here gates the wizard. What it buys is elsewhere and
 * later — the choice is remembered against the vendor's make/model strings,
 * so the next broker to meet the same wording gets it resolved for free.
 *
 * A failure is therefore silent. Losing the lesson is a missed optimisation,
 * not a problem the broker filling in a quote can do anything about.
 */
export function useConfirmVehicleVariant() {
  return useMutation({
    mutationFn: ({ registrationNumber, variantId }: { registrationNumber: string; variantId: string }) =>
      policyService.confirmVehicleVariant(registrationNumber, variantId),
    onError: () => {},
  });
}

/**
 * Read a registration number off a photo of the number plate or RC book.
 *
 * Free and offline — no vendor is called, nothing is billed. Returns
 * candidates for the user to confirm, because OCR misreads plate glyphs
 * (0/O, 8/B, 1/I) and a registration number has no checksum to catch it.
 */
export function useRegistrationOCR() {
  return useMutation({
    mutationFn: (file: File) => policyService.readRegistrationFromImage(file),
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function usePolicyDocuments(policyId: string, enabled = true) {
  return useQuery({
    queryKey: ['policyDocuments', policyId],
    queryFn: () => policyService.getPolicyDocuments(policyId),
    enabled: enabled && !!policyId,
  });
}

export function usePolicyPayments(policyId: string, enabled = true) {
  return useQuery({
    queryKey: ['policyPayments', policyId],
    queryFn: () => policyService.getPolicyPayments(policyId),
    enabled: enabled && !!policyId,
  });
}

export function useDeletePolicyDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ policyId, documentId }: { policyId: string; documentId: string }) =>
      policyService.deletePolicyDocument(policyId, documentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['policyDocuments', variables.policyId] });
      toast.success('Document deleted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// ── Premium-paying term (Life / Health / Term installments) ─────────────────
// One policy, one policy number, many installments. Every mutation below acts
// on the EXISTING policy, so each one invalidates the policy detail, the
// schedule and the timeline together — a payment that moved the schedule but
// left a stale "Next due" on screen is a page telling the user something
// untrue.

/** The policy's installment schedule.
 *
 * `enabled` lets the caller skip the request entirely for a policy that is not
 * paid in installments, so the panel is not asking a question it already knows
 * has no answer.
 */
export function usePolicyInstallments(policyId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['policy-installments', policyId],
    queryFn: () => policyService.getInstallments(policyId),
    enabled: (options?.enabled ?? true) && !!policyId,
  });
}

function invalidatePolicyAndSchedule(queryClient: ReturnType<typeof useQueryClient>, id: string) {
  queryClient.invalidateQueries({ queryKey: ['policies'] });
  queryClient.invalidateQueries({ queryKey: ['policy', id] });
  queryClient.invalidateQueries({ queryKey: ['policy-installments', id] });
  queryClient.invalidateQueries({ queryKey: ['policy-timeline', id] });
}

/** Build or extend the schedule. Settled installments are never rewritten. */
export function useGenerateInstallments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (policyId: string) => policyService.generateInstallments(policyId),
    onSuccess: (data, policyId) => {
      invalidatePolicyAndSchedule(queryClient, policyId);
      // The server's own count, not an assumption about what we asked for.
      toast.success(data.message ?? `${data.schedule.length} installment(s) scheduled`);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/** Record one installment as paid — the "renewal" of a premium-paying-term
 *  policy. Omit `installment_id` to settle the next one outstanding. */
export function useRecordInstallmentPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ policyId, data }: { policyId: string; data: RecordInstallmentPaymentRequest }) =>
      policyService.payInstallment(policyId, data),
    onSuccess: (data, { policyId }) => {
      invalidatePolicyAndSchedule(queryClient, policyId);
      toast.success(data.message ?? 'Installment recorded');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/** Reinstate a lapsed installment policy.
 *
 * The server refuses while arrears are outstanding and says which installments
 * are missing. That sentence is surfaced verbatim — a generic "could not
 * reinstate" would hide the one thing the broker needs to do next.
 */
export function useReinstatePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ policyId, notes }: { policyId: string; notes?: string }) =>
      policyService.reinstatePolicy(policyId, notes),
    onSuccess: (_, { policyId }) => {
      invalidatePolicyAndSchedule(queryClient, policyId);
      toast.success('Policy reinstated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
